import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import type { BackgroundJob } from "@/api/background-job/_models/BackgroundJob";
import { formatDateTime } from "@/app/date.utils";
import { NoteAlert } from "@/components/NoteAlert";
import { toJobDuration, toJobSummary } from "@/features/job/job.utils";

interface JobDetailStatusProps {
  job: BackgroundJob;
}

/** Where a job stands: waiting, running (with a moving bar and its time so far), finished with what it did, or failed with why. */
export const JobDetailStatus = (props: JobDetailStatusProps) => {
  const { job } = props;
  const [now, setNow] = useState(() => Date.now());
  const isActive = job.status === "QUEUED" || job.status === "RUNNING";

  useEffect(() => {
    if (!isActive) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isActive]);

  if (job.status === "DONE") {
    return (
      <NoteAlert severity="success">
        Finished in {toJobDuration(job, now)} · {toJobSummary(job)}
      </NoteAlert>
    );
  }
  if (job.status === "FAILED") {
    return (
      <NoteAlert severity="error">
        Failed: {job.error ?? "unknown error"}
      </NoteAlert>
    );
  }

  let text = `Queued ${formatDateTime(job.createdAt)} · waiting to start`;
  if (job.status === "RUNNING") text = `Running for ${toJobDuration(job, now)}`;

  return (
    <Box>
      <NoteAlert severity="info">{text}</NoteAlert>
      <LinearProgress
        sx={{
          borderRadius: 1,
          height: 4,
          mt: 1,
        }}
      />
    </Box>
  );
};
